import { useRouter } from "vue-router";
import { reactive, watch } from "vue";
import dayjs, { type Dayjs } from "dayjs";
import { useVehicleApi } from "./Api/VehicleApi";
import { useNotify, useConfirm } from "@library/Components";
import { useUserSettings } from "@services/UserSettings";
import { useConditionApi } from "./Api/ConditionApi";
import { ConditionState, ConditionType, ServiceIntervalState, Vehicle, type ICondition, type IConditionDto, type IServiceInterval, type IServiceSchedule, type IVehicle, type IVehicleEditableProperties } from "@domain/Vehicle";
import { SmsMessage, type ISmsMessage } from "@domain/Messaging";
import { Consumer, type IConsumer } from "@domain/Consumer";
import { Appointment, AppointmentType, type IAppointment } from "@domain/Appointment";
import { ServiceLogItem } from "@domain/Vehicle/ServiceLog/ServiceLogItem";
import { HistoryItem } from "@domain/Vehicle/History/HistoryItem";
import { AutoInstalledState } from "@domain/General/Enums/AutoInstalledState";
import { type ICarePlan, CarePlan } from "@domain/CarePlan";
import type { IServiceLogItem } from "@domain/Vehicle/ServiceLog/IServiceLogItem";
import type { IHistoryItem } from "@domain/Vehicle/History/IHistoryItem";
import type { IMyGarageViewModel, IMyGarageViewModelDto } from "..";
import { isDemo } from "@modules/Utilities/Environments";
import { useJupiterProps } from "@modules/Jupiter";

/**
 * The view model for the consumer profile
 */
export class MyGarageViewModel implements IMyGarageViewModel
{

    $updateKey: number = 0;

    consumer: IConsumer = {} as any;
    vehicle: IVehicle = {} as any;
    messages: ISmsMessage[] = [];
    appointments: IAppointment[] = [];
    serviceLog: IServiceLogItem[] = [];
    history: IHistoryItem[] = [];
    bookedServiceAppointment?: IAppointment;
    carePlan: ICarePlan = {} as any;
    isDirty: boolean = false;

    navigation = reactive( { activePanel: 'vehicle' } );

    constructor( model: IMyGarageViewModelDto )
    {
        this.initialise( model );
    }

    /**
     * Initialises the model from the DTO
     * @param model 
     */
    initialise( model: IMyGarageViewModelDto )
    {

        handleMockData( model );

        this.consumer = new Consumer( model.consumer );
        this.vehicle = new Vehicle( model.vehicle );
        this.messages = model.messages.map( m => new SmsMessage( m, this.vehicle ) );
        this.appointments = model.appointments.map( a => new Appointment( a, this.vehicle ) );
        this.serviceLog = ( model.serviceLog ?? [] ).map( i => new ServiceLogItem( i ) );
        this.history = ( model.history ?? [] ).map( i => new HistoryItem( i ) );
        this.carePlan = new CarePlan( model.carePlan );
        this.isDirty = false;

        this.bookedServiceAppointment = this.appointments.find( a => a.isOpen && a.appointmentType == AppointmentType.Service );

        // Get the current key from the route, and watch the key for changes, updating the route if it does
        this.navigation.activePanel = String( 'vehicle' );

        const userSettings = useUserSettings();
        if( userSettings.healthMonitorEnabledState == AutoInstalledState.Installed )
        {
            this.carePlan.healthMonitor.isPaired = true;
            if( userSettings.hasHealthMonitorWarning )
            {
                this.carePlan.healthMonitor.toggleMockErrorCode( true );
            }
        }

        this.vehicle.condition.applyCarePlan( this.carePlan );
        
    }
    
    /**
     * Occurs when the view model gets mounted into the MyGarage page
     */
    onMounted()
    {

        const router = useRouter();
        const jupiterProps = useJupiterProps();
        this.navigation.activePanel = String( router.currentRoute.value.query.p ?? 'vehicle' );
        
        watch( () => this.navigation.activePanel, activePanel =>
        {
            const query = { ...router.currentRoute.value.query, ...jupiterProps.getQuery(), ...{ p: activePanel } };
            router.replace( { query: query } );
        }, { immediate: true } );

    }

    /**
     * True if the connect to health monitor guide should be shown
     */
    get showConnectToHealthMonitorWorkflow()
    {

        const settings = useUserSettings();
        let isSkipped = false;
        
        // Check if there is a skip date and if that date is in the future
        if( settings.skipHealthMonitorReminderUntil != null )
        {
            isSkipped = dayjs( settings.skipHealthMonitorReminderUntil ) > dayjs()
        }

        // We're going to use these to decide if a Carbucks PROTECT customer should be reminded
        // to connect their health monitor.  We wait 48 hours for these because
        // we dont want them to be asked if they are sitting with a Business Manager
        const hasCarbucksProtect = this.carePlan.carbucksProtect.hasCarbucksProtect;
        const daysSinceWelcomeFlowCompleted = this.carePlan.utcDateWelcomeFlowCompleted == null 
            ? 0 
            : dayjs().diff( this.carePlan.utcDateWelcomeFlowCompleted, 'days' );

        return true

            // The user has NOT marked the health monitor as NotInstalled
            && settings.healthMonitorEnabledState !== AutoInstalledState.NotInstalled

            // The user has NOT skipped the health monitor reminder
            && isSkipped == false 

            // We're not paired or are not receiving telemetry data
            && this.carePlan.healthMonitor.showHealthMonitorDisconnectedWorflow

            // THEN

            // If the car has Carbucks PROTECT, wait 48 hours before showing the connect,
            // otherwise show the connection workflow
            && ( ( hasCarbucksProtect && daysSinceWelcomeFlowCompleted > 1 ) || hasCarbucksProtect == false )

            ;

    }


    /**
     * Occurs when the vehicle details get updated
     * @param vehicleProperties
     * @returns
     */
    async onVehicleDetailsUpdatedAsync( vehicleProperties: IVehicleEditableProperties ): Promise<boolean>
    {
        const api = useVehicleApi();
        const result = await api.updateVehicleDetailsAsync( vehicleProperties );
        if( result != null ) this.vehicle.condition.initialise( result.conditions );
        this.$updateKey += 1; // Forces the bound components to re-render

        return Promise.resolve( true );
    }

    /**
     * Occurs when the user marks one of their conditions as done
     * @param condition
     */
    async onConditionMarkDoneAsync( condition: ICondition ): Promise<boolean>
    {
        const api = useConditionApi();
        const response = await api.advanceByConditionInterval( condition.conditionId );

        // Re-initialise the vehicle condition
        if( response?.condition != null ) this.vehicle.condition.initialise( [response.condition] );
        if( response?.historyItem != null ) this.history.push( new HistoryItem( response.historyItem ) );
        
        this.$updateKey += 1; // Forces the bound components to re-render

        return Promise.resolve( true );
    }

    /**
     * Occurs when the user sets the next date for one of their conditions
     * @param condition
     * @param nextDate
     * @param advanceFromToday
     */
    async onConditionSetNextAsync( condition: ICondition, nextDate: Dayjs, advanceFromToday: boolean = false ): Promise<boolean>
    {
        const api = useConditionApi();
        const response = await api.setNext( condition.conditionId, nextDate, advanceFromToday );
        const conditionDto = response?.condition;

        // Re-initialise the vehicle condition
        if( conditionDto != null ) this.vehicle.condition.initialise( [conditionDto] );

        this.$updateKey += 1; // Forces the bound components to re-render

        return Promise.resolve( true );

    }

    /**
     * Occurs when the user selects a service interval to update their service status
     * @param condition
     */
    async onConditionSetServiceIntervalAsync( condition: ICondition, interval: IServiceInterval, schedule: IServiceSchedule ): Promise<boolean>
    {
        let lastOdometer = 0;
        let lastMonths = 0;

        // Set the last position to be the scheduled interval prior to the selection, or zero
        const priorIntervals = schedule.intervals.filter( x => x.km < interval.km );
        if( priorIntervals.length > 0 )
        {
            const priorInterval = priorIntervals[ priorIntervals.length - 1 ];
            lastOdometer = priorInterval.km;
            lastMonths = priorInterval.months;
        }

        const api = useConditionApi();
        const response = await api.setServiceInterval( condition.conditionId, interval, lastOdometer, lastMonths );

        // Re-initialise the vehicle condition
        if( response != undefined )
        {
            this.vehicle.condition.initialise( [ response.condition ] );

            // update the next interval in the cached service schedule
            this.vehicle.condition.serviceSchedule.nextInterval = interval;

            if ( response.historyItem )
            {
                // add an event to the vehicle history
                this.history.push( new HistoryItem( response.historyItem ) );
            }

            // Forces the bound components to re-render
            this.$updateKey += 1;
        }

        return Promise.resolve( true );
    }

    /**
     * Occurs when a critical issue is marked as resolved by the user
     * @param condition
     */
    async onConditionResolveAsync( condition: ICondition ): Promise<boolean>
    {

        const api = useConditionApi();
        const conditionDto = await api.resolve( condition.conditionId );

        // Re-initialise the vehicle condition
        if( conditionDto != null ) this.vehicle.condition.initialise( [conditionDto] );
        this.$updateKey += 1; // Forces the bound components to re-render

        return Promise.resolve( true );

    }

    /**
     * Occurs when a critical condition is dismissed by the user
     * @param condition
     */
    async onConditionDismissAsync( condition: ICondition ): Promise<boolean>
    {

        const api = useConditionApi();
        const conditionDto = await api.dismiss( condition.conditionId );

        // Re-initialise the vehicle condition
        if( conditionDto != null ) this.vehicle.condition.initialise( [conditionDto] );
        this.$updateKey += 1; // Forces the bound components to re-render

        return Promise.resolve( true );

    }

    /**
     * Occurs when an appointment gets cancelled
     * @param condition
     */
    async onCancelAppointmentAsync( appointment: IAppointment ): Promise<boolean>
    {
        this.appointments = this.appointments.filter( a => a != appointment );
        this.bookedServiceAppointment = undefined;
        // TODO: Implement API logic
        return Promise.resolve( true );
    }

    /**
     * Marks the model as dirty so that it will be reloaded from the server not the cache,
     * the next time that it's requested
     */
    markDirty()
    {
        this.isDirty = true;
    }

    /**
     * Displays a tooltip for a condition flag
     * @param condition
     */
    showFlagTip( condition: ICondition )
    {

        if( condition.isUnconfigured )
        {
            useNotify( "This indicates that the condition has not been configured.  You can configure the condition from the My Garage page, and once it's set, Carbucks can help you maintain the condition of your vehicle.", "Not Set" );
        }

        else if( condition.type == ConditionType.Recall )
        {
            useNotify( "This indicates that there is a condition that requires urgent attention.  You have a recall notice issued on your car, and you should book an appointment with your Dealership as soon as possible to have it repaired.", "Recall Notice" );
        }

        else if( condition.isCritical )
        {
            useNotify( "Your Carbucks Health Monitor has detected an issue with your vehicle, and you should book an appointment with your Dealership as soon as possible to have it examined.", "Health Monitor Alert" );
        }

        else if( condition.isOverdue )
        {
            useNotify( "This activity is now overdue and may affect the safety or value of your vehicle!  You should address it as soon as you are able to.", "Overdue Maintenance" );
        }

        else if( condition.isDue )
        {
            useNotify( "This activity is coming up!  You don't need to take action right away, but if it requires professional attention, you may consider booking an appointment with your preferred service provider.", "Maintenance Due" );
        }

        else
        {
            useNotify( "Indicates that this condition is healthy and no maintenance tasks are required.", "Healthy!" );
        }

    }

}

const handleMockData = ( model: IMyGarageViewModelDto ) =>
{

    if( isDemo() )
    {
        // Prevents the 'update your odometer' screen from showing.
        model.vehicle.dateLastSetOdometer = dayjs().format( "YYYY-MM-DD" );

        model.vehicle.condition.serviceSchedule.intervals.forEach( interval =>
        {
            // Ensure that the first 1 month service was 'completed' 11 months ago.
            if ( interval.months == 1 )
            {
                interval.state = ServiceIntervalState.MarkedDoneFromDms;
                interval.dateCompleted = dayjs().add( -11, "months" ).format( "YYYY-MM-DD" );
            }
            else
            {
                interval.state = ServiceIntervalState.NotDone;
            }
        } );

        model.vehicle.condition.serviceSchedule.nextInterval = model.vehicle.condition.serviceSchedule.intervals.find( interval => interval.state == ServiceIntervalState.NotDone );
        model.carePlan.carbucksProtect.dateActiveFrom = dayjs().add( -1, "months" ).format( "YYYY-MM-DD" );
        model.carePlan.carbucksProtect.dateActiveTo = dayjs().add( -1, "months" ).add( 3, "years" ).format( "YYYY-MM-DD" );

        updateConditionType( model, ConditionType.Service, serviceCondition =>
        {
            serviceCondition.state = ConditionState.Due;
            serviceCondition.nextDate = dayjs().add( 5, 'months' ).format( "YYYY-MM" ) + "-01";
            serviceCondition.nextOdometer = 15000;
        } );
        
        updateConditionType( model, ConditionType.Battery, batteryCondition =>
        {
            batteryCondition.state = ConditionState.UpToDate;
            batteryCondition.nextDate = dayjs().add( 30, 'months' ).format( "YYYY-MM" ) + "-01";
        } );

        updateConditionType( model, ConditionType.Tyres, tyresCondition =>
        {
            tyresCondition.state = ConditionState.UpToDate;
            tyresCondition.nextDate = dayjs().add( 15, 'months' ).format( "YYYY-MM" ) + "-01";
        } );

        updateConditionType( model, ConditionType.Rego, regoCondition =>
        {
            regoCondition.state = ConditionState.UpToDate;
            regoCondition.nextDate = ( model.vehicle.regoExpiryDate ? dayjs( model.vehicle.regoExpiryDate ) : dayjs().add( 5, "months" ) ).format( "YYYY-MM-DD" );;
        } );

        updateConditionType( model, ConditionType.WiperBlades, wiperBladeCondition =>
        {
            wiperBladeCondition.state = ConditionState.UpToDate;
            wiperBladeCondition.nextDate = dayjs().add( 2, 'months' ).format( "YYYY-MM" ) + "-01";
        } );

        updateConditionType( model, ConditionType.Wash, washCondition =>
        {
            washCondition.state = ConditionState.UpToDate;
            washCondition.nextDate = dayjs().add( 2, 'months' ).format( "YYYY-MM" ) + "-01";
        } );
    }
}

const updateConditionType = (model: IMyGarageViewModelDto, conditionType: ConditionType, action: ( condition: IConditionDto ) => void ) =>
{
    const condition = model.vehicle.condition.conditions.find( condition => condition.type == conditionType );

    if( condition != null )
    {
        action( condition );
    }
}